import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { AuthGate } from '@/components/auth/auth-gate'
import { AuthProvider } from '@/components/auth/auth-provider'
import { HandOverReturn, InvitationRoute, type JoinedState } from '@/components/invitation/invitation-route'
import { AccessGate } from '@/components/tenant/access-gate'
import { LanguageProvider } from '@/components/language/language-context'
import { LanguageSelector } from '@/components/language/language-selector'
import { TenantDataBoundary, useTenantContext } from '@/components/tenant/tenant-context'
import { TenantGate } from '@/components/tenant/tenant-gate'
import { TenantSwitcher } from '@/components/tenant/tenant-switcher'
import { VenueScopeProvider } from '@/components/today/venue-scope-context'
import { TodayDashboard } from '@/components/today/today-dashboard'
import { Button } from '@/components/ui/button'
import { Notice } from '@/components/ui/notice'
import { AppShell } from '@/components/visitone/app-shell'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { UnsavedChangesProvider, useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import type { PaletteFeature, PaletteRoute } from '@/components/visitone/command-palette'
import { NoAreasSurface, RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { GuestDataAccessSurface } from '@/components/visitone/surfaces/guest-data-access-surface'
import { GuestRecordSurface } from '@/components/visitone/surfaces/guest-record-surface'
import { GuestsSurface } from '@/components/visitone/surfaces/guests-surface'
import { LandlordsSurface, type LandlordsRouteState } from '@/components/visitone/surfaces/landlords-surface'
import { OrderTypeSurface } from '@/components/visitone/surfaces/order-type-surface'
import { AccessChangesSurface } from '@/components/visitone/surfaces/people/access-changes-surface'
import { PeopleRolesSurface } from '@/components/visitone/surfaces/people/people-roles-surface'
import {
  PeopleUsersSurface,
  type PeopleRouteState,
} from '@/components/visitone/surfaces/people/people-users-surface'
import { PlaceholderSurface } from '@/components/visitone/surfaces/placeholder-surface'
import { SetupIndexSurface } from '@/components/visitone/surfaces/setup-index-surface'
import { VisitOneProvider, useT } from '@/components/visitone/visitone-provider'
import type { DestinationId } from '@/components/visitone/navigation-model'
import { useAccess } from '@/hooks/use-access'
import { homeTarget, PLAN_LIMITED_FEATURES } from '@/lib/access'
import { offeredGuestLists } from '@/lib/guests/guest-lists'
import { signOut } from '@/lib/auth/session'
import {
  GUESTS_PATH,
  guestListPath,
  nearestBuiltPath,
  pathForDestination,
  routeDestination,
  SETUP_GUEST_DATA_ACCESS_PATH,
  SETUP_LANDLORDS_PATH,
  SETUP_ORDER_TYPES_PATH,
  SETUP_PEOPLE_CHANGES_PATH,
  SETUP_PEOPLE_PATH,
  SETUP_PEOPLE_ROLES_PATH,
} from '@/lib/routing'
import { useAuth0 } from '@auth0/auth0-react'
import * as React from 'react'

function AuthenticatedShell() {
  return (
    <UnsavedChangesProvider>
      <ShellFrame />
    </UnsavedChangesProvider>
  )
}

/*
 * Renders a route's page only when the caller may open its destination (and
 * hold `action`, if given). An action with no destination is a principal
 * action such as `landlords.manage`, which is not granted by a tenant role.
 * Otherwise the page never mounts, so nothing from the area is requested
 * (VIS-329 AC 3, AC 8).
 */
function Gate({
  destination,
  action,
  feature,
  children,
}: {
  destination?: DestinationId
  action?: string
  feature?: string
  children: React.ReactNode
}) {
  const { canView, can, hasFeature } = useAccess()
  if (destination === undefined && action === undefined) return <RefusedSurface />
  if ((destination !== undefined && !canView(destination)) || (action !== undefined && !can(action))) {
    return <RefusedSurface />
  }
  if (feature !== undefined && !hasFeature(feature)) return <RefusedSurface />
  return <>{children}</>
}

/* Setup's index is also the way into Landlords, so the principal action opens it without the setup destination. */
function SetupHomeGate({ children }: { children: React.ReactNode }) {
  const { canOpenSetupHome } = useAccess()
  if (!canOpenSetupHome) return <RefusedSurface />
  return <>{children}</>
}

/*
 * `/` lands on Today, otherwise the first permitted destination, otherwise
 * Landlords when that action is held. Router state (such as the tenant just
 * joined from an invitation) is carried to that page.
 */
function Landing() {
  const { access } = useAccess()
  const location = useLocation()
  const home = homeTarget(access)
  if (!home) return <NoAreasSurface />
  return <Navigate to={home.path} replace state={location.state} />
}

/* After accepting an invitation, the first page says which tenant was joined. */
function JoinedNotice() {
  const t = useT()
  const location = useLocation()
  const joined = (location.state as JoinedState | null)?.joinedTenant
  if (!joined) return null
  return (
    <Notice tone="info" live title={t('people.joined.title', { tenant: joined })} className="mb-4 max-w-5xl">
      {t('people.joined.body')}
    </Notice>
  )
}

function ShellFrame() {
  const t = useT()
  const { user, logout } = useAuth0()
  const { confirmThen } = useUnsavedChanges()
  const location = useLocation()
  const navigate = useNavigate()
  const access = useAccess()
  const { canView, can, hasFeature, destinations, railDestinations } = access
  const canManageLandlords = can('landlords.manage')
  const canProduceGuestAccess = can('guest_access.produce')
  const canOpenSetup = canView('setup')
  const canViewPeople = canOpenSetup && can('users.view')
  const canInvite = canViewPeople && can('access.manage')
  const routed = routeDestination(location.pathname)
  const onLandlords = location.pathname === SETUP_LANDLORDS_PATH
  const onGuestDataAccess = location.pathname === SETUP_GUEST_DATA_ACCESS_PATH
  const onSetupHome = location.pathname === '/setup'
  const actionRefused = (onLandlords && !canManageLandlords) || (onGuestDataAccess && !canProduceGuestAccess)
  const current =
    routed &&
    !actionRefused &&
    (canView(routed) ||
      (routed === 'setup' &&
        ((canManageLandlords && (onSetupHome || onLandlords)) || (canProduceGuestAccess && onGuestDataAccess))))
      ? routed
      : null
  const { phase, target, from, retry, goBack, announcement, arrival } = useTenantContext()
  const navigationInert = phase !== 'ready'

  // After a switch arrives, focus moves to the new page's heading.
  React.useEffect(() => {
    if (arrival === 0) return
    const frame = requestAnimationFrame(() => {
      const heading = document.querySelector<HTMLElement>('main h1')
      if (!heading) return
      heading.setAttribute('tabindex', '-1')
      heading.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [arrival])

  const account = React.useMemo(
    () => ({
      name: user?.name ?? user?.nickname,
      email: user?.email,
      onLogout: () => confirmThen({ kind: 'logout' }, () => void signOut(logout)),
    }),
    [user, logout, confirmThen],
  )

  const onNavigate = React.useCallback(
    (id: DestinationId) => {
      navigate(pathForDestination(id))
    },
    [navigate],
  )

  const onOpenRoute = React.useCallback(
    (route: string, state?: unknown) => {
      navigate(nearestBuiltPath(route), state === undefined ? undefined : { state })
    },
    [navigate],
  )

  const canOpenGuests = canView('guests')
  const paletteRoutes = React.useMemo<PaletteRoute[]>(
    () => [
      ...(canOpenGuests
        ? offeredGuestLists(hasFeature).map((list) => ({
            key: `route-guests-${list}`,
            // The palette always opens Arrivals on today (VIS-490), so it keeps that name.
            label: list === 'arrivals' ? t('guests.palette.arrivalsToday') : t(`guests.lists.names.${list}`),
            route: guestListPath(list),
          }))
        : []),
      ...(canOpenSetup
        ? [{ key: 'route-order-types', label: t('core.nav.orderTypes'), route: SETUP_ORDER_TYPES_PATH }]
        : []),
      ...(canViewPeople ? [{ key: 'route-people', label: t('people.palette.area'), route: SETUP_PEOPLE_PATH }] : []),
      ...(canInvite
        ? [
            {
              key: 'route-invite',
              label: t('people.palette.invite'),
              route: SETUP_PEOPLE_PATH,
              state: { openInvite: true } satisfies PeopleRouteState,
            },
          ]
        : []),
      ...(canManageLandlords
        ? [
            { key: 'route-landlords', label: t('landlords.title'), route: SETUP_LANDLORDS_PATH },
            {
              key: 'route-add-landlord',
              label: t('landlords.add'),
              route: SETUP_LANDLORDS_PATH,
              state: { openAddLandlord: true } satisfies LandlordsRouteState,
            },
          ]
        : []),
      ...(canProduceGuestAccess
        ? [{ key: 'route-guest-data-access', label: t('setup.areas.guestAccess.name'), route: SETUP_GUEST_DATA_ACCESS_PATH }]
        : []),
    ],
    [t, canOpenGuests, hasFeature, canOpenSetup, canViewPeople, canInvite, canManageLandlords, canProduceGuestAccess],
  )

  const paletteFeatures = React.useMemo<PaletteFeature[]>(
    () =>
      (access.access?.features ?? []).flatMap((feature) => {
        const known = PLAN_LIMITED_FEATURES[feature.id]
        if (!known) return []
        return [{ key: `feature-${feature.id}`, label: t(known.labelKey), ...known }]
      }),
    [access.access, t],
  )

  let content: React.ReactNode
  if (phase === 'switching') {
    content = (
      <SwitchStatus>
        <OperationFeedback
          state="loading"
          title={t('core.tenant.switchingTitle', { to: target?.name ?? '' })}
          pageHeading
          detail={t('core.tenant.switchingDetail', { to: target?.name ?? '' })}
        />
      </SwitchStatus>
    )
  } else if (phase === 'failed') {
    content = (
      <SwitchStatus>
        <OperationFeedback
          state="error"
          title={t('core.tenant.failedTitle', { to: target?.name ?? '' })}
          pageHeading
          detail={t('core.tenant.failedDetail')}
          onRetry={retry}
        />
        {from && from.id !== target?.id ? (
          <div>
            <Button variant="secondary" onClick={goBack}>
              {t('core.tenant.failedBack', { from: from.name })}
            </Button>
          </div>
        ) : null}
      </SwitchStatus>
    )
  } else if (!homeTarget(access.access)) {
    content = <NoAreasSurface />
  } else {
    content = (
      <TenantDataBoundary>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route
            path="/today"
            element={
              <Gate destination="today">
                <TodayDashboard onOpenRoute={onOpenRoute} />
              </Gate>
            }
          />
          <Route
            path={GUESTS_PATH}
            element={
              <Gate destination="guests">
                <GuestsSurface />
              </Gate>
            }
          />
          <Route
            path={`${GUESTS_PATH}/:guestId`}
            element={
              <Gate destination="guests">
                <GuestRecordSurface />
              </Gate>
            }
          />
          <Route
            path="/setup"
            element={
              <SetupHomeGate>
                <SetupIndexSurface
                  canManageLandlords={canManageLandlords}
                  canOpenSetup={canOpenSetup}
                  canViewPeople={canViewPeople}
                  canProduceGuestAccess={canProduceGuestAccess}
                  tenantName={access.tenantName}
                />
              </SetupHomeGate>
            }
          />
          <Route
            path={SETUP_ORDER_TYPES_PATH}
            element={
              <Gate destination="setup">
                <OrderTypeSurface />
              </Gate>
            }
          />
          <Route
            path={SETUP_PEOPLE_PATH}
            element={
              <Gate destination="setup" action="users.view">
                <PeopleUsersSurface />
              </Gate>
            }
          />
          <Route
            path={SETUP_PEOPLE_ROLES_PATH}
            element={
              <Gate destination="setup" action="users.view">
                <PeopleRolesSurface />
              </Gate>
            }
          />
          <Route
            path={SETUP_PEOPLE_CHANGES_PATH}
            element={
              <Gate destination="setup" action="access_changes.view">
                <AccessChangesSurface />
              </Gate>
            }
          />
          <Route
            path={SETUP_LANDLORDS_PATH}
            element={
              <Gate action="landlords.manage">
                <LandlordsSurface />
              </Gate>
            }
          />
          <Route
            path={SETUP_GUEST_DATA_ACCESS_PATH}
            element={
              <Gate action="guest_access.produce">
                <GuestDataAccessSurface />
              </Gate>
            }
          />
          <Route
            path="/setup/*"
            element={
              <SetupHomeGate>
                <Navigate to="/setup" replace />
              </SetupHomeGate>
            }
          />
          <Route
            path="/programme"
            element={
              <Gate destination="programme">
                <PlaceholderSurface title={t('core.nav.programme')} />
              </Gate>
            }
          />
          <Route
            path="/engagement"
            element={
              <Gate destination="engagement">
                <PlaceholderSurface title={t('core.nav.engagement')} />
              </Gate>
            }
          />
          <Route
            path="/engagement/fixture-moments"
            element={
              <Gate destination="engagement" feature="fixtureMoments">
                <PlaceholderSurface title={t('core.nav.fixtureMoments')} />
              </Gate>
            }
          />
          <Route
            path="/insights"
            element={
              <Gate destination="insights">
                <PlaceholderSurface title={t('core.nav.insights')} />
              </Gate>
            }
          />
          <Route path="*" element={<RefusedSurface />} />
        </Routes>
      </TenantDataBoundary>
    )
  }

  return (
    <VenueScopeProvider>
      <LanguageProvider>
        <AppShell
          current={current}
          destinations={destinations}
          railDestinations={railDestinations}
          onNavigate={onNavigate}
          onOpenRoute={onOpenRoute}
          paletteRoutes={paletteRoutes}
          paletteFeatures={paletteFeatures}
          hasFeature={hasFeature}
          account={account}
          navigationInert={navigationInert}
          tenantSlot={<TenantSwitcher />}
          languageSlot={
            phase === 'ready' ? (
              <TenantDataBoundary>
                <LanguageSelector />
              </TenantDataBoundary>
            ) : null
          }
        >
          <p className="sr-only" role="status" aria-live="polite">
            {announcement}
          </p>
          {phase === 'ready' ? <JoinedNotice /> : null}
          {content}
        </AppShell>
      </LanguageProvider>
    </VenueScopeProvider>
  )
}

/* Holds the switching and failed views; focus moves here so it is not lost when tenant content goes. */
function SwitchStatus({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    ref.current?.focus()
  }, [])
  return (
    <div className="flex min-h-full items-start justify-center pt-16">
      <div ref={ref} tabIndex={-1} className="flex w-full max-w-xl flex-col gap-3 outline-none">
        {children}
      </div>
    </div>
  )
}

function RootRoutes() {
  return (
    <AuthGate>
      <TenantGate>
        <AccessGate>
          <AuthenticatedShell />
        </AccessGate>
      </TenantGate>
    </AuthGate>
  )
}

/*
 * A data router, because the unsaved-changes guard blocks navigation with
 * `useBlocker`. The invitation page sits outside AuthGate: it is open to a
 * person with no session, and chooses what to show from whether one exists.
 */
function AppRouter() {
  const [router] = React.useState(() =>
    createBrowserRouter([
      { path: '/invitation/:token', element: <InvitationRoute /> },
      {
        path: '*',
        element: (
          <HandOverReturn>
            <RootRoutes />
          </HandOverReturn>
        ),
      },
    ]),
  )
  return <RouterProvider router={router} />
}

export function App() {
  return (
    <VisitOneProvider>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </VisitOneProvider>
  )
}
